import { useEffect, useState } from 'react';
import { apiRelatorios, type AberturaDoCaso } from '../lib/api.ts';
import { usarEhMaster } from '../lib/papel.ts';
import { avisarErro } from './Avisos.tsx';
import { SumarioDoRelatorio, type CampoDoRetorno } from './SumarioDoRelatorio.tsx';
import { Traco } from './ImoveisDoCaso';
import '../estilos/linha-rapida-2026.css';
import '../estilos/imoveis-2026.css';

/**
 * A abertura do relatório — o que o Hub propõe para o capítulo 1.
 *
 * > *"Um quadro que o Hub desenha sozinho, e também, um texto que o Hub
 * > propõe."* — Richard, 07/09/2026
 *
 * ## ⭐⭐ São DOIS caminhos, e ficam os dois — 11/09/2026
 *
 * > *"E se ao invés do botão 'Propor o texto desta seção' […] substituirmos
 * > para o botão de gerar prompt?"* — Richard, 10/09/2026
 *
 * ⚠️ **Ele perguntou se um SUBSTITUI o outro, e a resposta é não.** Os dois
 * respondem a perguntas diferentes:
 *
 * | | Serve quando |
 * |---|---|
 * | **Propor o texto** | o caso é simples e basta a frase montada dos marcos da dívida — sai na hora, sem sair da casa |
 * | **Gerar o prompt** | ele quer o capítulo **escrito**, com a voz da casa, e aceita levar o material ao Claude ou ao GPT |
 *
 * ⛔ **Tirar um dos dois seria decidir por ele em qual dos dois casos está** —
 * e a proposta já existe, já é usada e não custa nada manter.
 *
 * ⛔ **E há uma diferença que a tela precisa deixar clara:** a proposta **não
 * sai da KAIRÓS**; o prompt **sai ao ser colado**. O aviso mora dentro da
 * máquina, e é por isso que ela é a mesma das outras quatro peças.
 *
 * ## ⚠️ Máquina propõe, gente aceita
 *
 * O botão **não grava**: ele mostra. Quem grava é o segundo botão, e ele diz na
 * cara que substitui o que estiver escrito. É o mesmo desenho do leitor de
 * contrato social, e pela mesma razão: **nome não tem dígito verificador**, e
 * nome errado passa calado para um documento assinado.
 *
 * ## ⚠️ O quadro é do documento, não da tela
 *
 * A tabela abaixo é a mesma que sai impressa — desenhada pelo gerador a cada
 * geração, lendo o banco. **Ela não se grava na seção**, e é por isso que nunca
 * desatualiza quando alguém entra na ficha do caso. O que o botão grava é só o
 * texto.
 *
 * ## ⚠️ Por que o valor da dívida aparece sem máscara
 *
 * `dividaValor` **não é dinheiro da KAIRÓS**: é o que o cliente tem a receber
 * do devedor — o tamanho do problema, não o preço do serviço. A decisão de
 * mostrá-lo a todos está registrada em `MarcosDaDivida.tsx`, e o olho do
 * cabeçalho continua guardando o honorário, que mora só no contrato.
 */
export function AberturaDoRelatorio({
  relatorioId,
  /** `objeto` ou `partes` — o que esta seção mostra. */
  peca,
  /** Mostra o quadro das partes abaixo do texto. Só a seção do escopo pede. */
  comQuadro,
  travado,
  /** Já há texto escrito na seção? Muda o aviso do botão que grava. */
  jaEscrito,
  secaoId,
  aoGravar,
  /** ⭐ 23/09/2026 — o passo "O texto que voltou" vira campo de escrita. */
  campo,
  /** Muda quando a ficha do caso muda, para a proposta se refazer. */
  chave,
}: {
  relatorioId: string;
  campo?: CampoDoRetorno;
  /**
   * ⭐ `'partes'` entrou em 09/09/2026, quando o ESCOPO se separou das PARTES:
   * nesta peça **não há texto a propor** — o quadro é desenhado do cadastro e
   * nunca desatualiza.
   *
   * ⛔ `'escopo'` saiu em 08/10/2026: a seção foi excluída da estrutura em
   * 25/09/2026, e nenhum relatório a tem mais.
   */
  peca: 'objeto' | 'partes';
  comQuadro?: boolean;
  travado: boolean;
  jaEscrito: boolean;
  secaoId: string;
  aoGravar: () => Promise<void>;
  chave?: string;
}) {
  const [abertura, setAbertura] = useState<AberturaDoCaso | null>(null);
  const [mostrando, setMostrando] = useState(false);
  const [gravando, setGravando] = useState(false);
  const ehMaster = usarEhMaster();

  /*
   * ⚠️ **Carrega sozinho, e não custa nada.** O capítulo 1 é inteiramente dado
   * que já está na casa — nenhuma consulta paga acontece aqui. Fosse consulta
   * externa, isto seria um botão, como manda a regra do custo à vista.
   */
  useEffect(() => {
    let vivo = true;
    apiRelatorios
      .abertura(relatorioId)
      .then((a) => {
        if (vivo) setAbertura(a);
      })
      .catch(() => {
        if (vivo) setAbertura(null);
      });
    return () => {
      vivo = false;
    };
  }, [relatorioId, chave]);

  /* ⚠️ `partes` não tem proposta de texto: só o quadro. Pedir `abertura['partes']`
     devolveria a LISTA de partes onde se espera uma string — e a tela mostraria
     "[object Object]" como se fosse a proposta. */
  const texto = abertura && peca !== 'partes' ? abertura[peca] : null;

  async function gravar() {
    if (!texto) return;
    setGravando(true);
    try {
      await apiRelatorios.acrescentar(relatorioId, secaoId, texto);
      await aoGravar();
      setMostrando(false);
    } catch (e) {
      avisarErro(e);
    } finally {
      setGravando(false);
    }
  }

  /*
   * ⚠️ **Fragmento, e não uma `div` de classe própria.** Um invólucro sem
   * estilo é peso morto no HTML e classe que alguém um dia procura no CSS e
   * não acha. Os blocos de dentro já trazem o espaçamento da casa.
   */
  /*
   * ⭐ **No desenho de Imóveis desde 19/09/2026 (PRD §46.8).** O quadro virou a
   * tabela da casa; a proposta ganhou a faixa com o que ela é — e a frase que a
   * distingue do prompt logo abaixo: **a proposta não sai da KAIRÓS**. Os
   * botões, os textos e as rotas são os de antes.
   */
  return (
    <>
      {comQuadro && abertura && abertura.partes.length > 0 && (
        <section className="imv-quadro">
          <header className="imv-quadro-cab">
            <span className="imv-rot">As partes investigadas, como saem no documento</span>
          </header>
          <div className="imv-quadro-rola">
            <table>
              <thead>
                <tr>
                  <th>Parte</th>
                  <th>Documento</th>
                  <th>Qualificação</th>
                </tr>
              </thead>
              <tbody>
                {abertura.partes.map((p) => (
                  <tr key={`${p.documento || p.nome}`}>
                    <td>{p.nome}</td>
                    {/* ⚠️ Documento é dado técnico: monoespaçada. Travessão quando
                        não há — célula vazia num quadro lê-se como esquecimento. */}
                    <td className="dado">{p.documento || '—'}</td>
                    <td>{p.qualificacao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* ⚠️ Dito na tela para ninguém procurar o botão de gravar o quadro:
              ele não existe, e é isso que impede a tabela de envelhecer. */}
          <p className="imv-quadro-pe">
            Este quadro sai impresso direto do cadastro do caso — não precisa ser
            gravado, e acompanha sozinho quem entrar ou sair da ficha.
          </p>
        </section>
      )}

      {/*
        ⚠️ **Proposta nula é lacuna DECLARADA, nunca botão morto.** Sem marco de
        dívida não há frase a escrever, e um botão que produzisse texto vazio
        seria pior que a ausência: quem clicasse acharia que gravou algo.
      */}
      {/* Travado, a faixa só aparece para dizer a lacuna — o botão some, como antes. */}
      {abertura && peca !== 'partes' && !mostrando && (!travado || !texto) && (
        <div className="imv-faixa-acao">
          <span className="imv-faixa-ic" aria-hidden="true">
            <Traco d={['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5', 'M9 13h6', 'M9 16.5h4']} />
          </span>
          <div className="imv-faixa-txt">
            <span className="imv-rot">Proposta do Hub</span>
            <p>
              {texto
                ? 'A frase montada com os dados do caso, na hora — e sem sair da KAIRÓS.'
                : /*
                   * ⚠️ **Diz "a proposta", não "o texto" — desde 11/09/2026.** Com o
                   * prompt logo abaixo, a frase antiga faria parecer que a seção
                   * inteira está bloqueada, quando só o atalho é que não dá.
                   */
                  peca === 'objeto'
                  ? 'A proposta pronta precisa dos marcos: o caso ainda não tem valor de dívida, datas nem processo. Preencha "Sobre a dívida", na ficha do caso — ou use o prompt abaixo.'
                  : 'A proposta pronta precisa das partes: o caso ainda não tem parte investigada cadastrada.'}
            </p>
          </div>
          {texto && !travado && (
            <button type="button" className="imv-bt" onClick={() => setMostrando(true)}>
              Propor o texto desta seção
            </button>
          )}
        </div>
      )}

      {mostrando && texto && (
        <div className="imv-painel">
          <p className="imv-painel-titulo">O que vai para a seção</p>
          <pre className="previa-da-secao">{texto}</pre>
          <div className="abertura-acoes">
            <button
              type="button"
              className="imv-bt primario"
              onClick={() => void gravar()}
              disabled={gravando}
            >
              {gravando ? 'Gravando…' : jaEscrito ? 'Acrescentar ao fim desta seção' : 'Preencher a seção com este texto'}
            </button>
            <button type="button" className="imv-bt fantasma" onClick={() => setMostrando(false)}>
              Descartar
            </button>
          </div>
          {/* ⛔ Nenhum botão apaga o que o caso já tem (decisão dele, 15/09/2026):
              com a seção escrita, a proposta vai para o fim do texto. */}
          <p className="ajuda">
            {jaEscrito
              ? 'Vai para o fim do que já está escrito nesta seção; nada é apagado. '
              : ''}
            Revise antes de entregar — o texto é montado pelo Hub e o documento é
            assinado por gente.
          </p>
        </div>
      )}

      {/*
        ⭐⭐ **O SEGUNDO CAMINHO: gerar o prompt — 11/09/2026.**

        ⚠️ **É a MESMA máquina das outras quatro peças**, recebida por `fonte`.
        Uma tela própria para o capítulo 1 divergiria da do sumário na primeira
        correção feita só numa delas — e o aviso de que o dado sai da KAIRÓS
        mora lá dentro, onde ele precisa continuar morando.

        ⛔ **Não aparece quando o relatório está travado**, como o outro botão.

        ⚠️ **Aparece mesmo sem proposta a fazer, e é deliberado.** A proposta do
        objeto precisa dos marcos da dívida; o prompt funciona com menos — e
        quando não funciona, ele mesmo devolve a frase que diz o que falta, em
        vez de um texto inventado.
      */}
      {/* ⛔ Só do master (19/09/2026): a máquina se esconde sozinha do
          colaborador, e aqui não sobra a caixa vazia em volta dela. */}
      {!travado && ehMaster && (
        <div>
          <SumarioDoRelatorio
            travado={travado}
            campo={campo}
            redacao={peca}
            fonte={{
              apoio: APOIO_DO_PROMPT[peca],
              rotuloGravar: jaEscrito
                ? 'Acrescentar ao fim desta seção'
                : 'Gravar na seção',
              gerar: async () => (await apiRelatorios.promptDaAbertura(relatorioId, peca)).prompt,
              conferir: async (texto) =>
                (await apiRelatorios.conferirSumario(relatorioId, texto)).achados,
            }}
            aoAceitar={async (texto) => {
              await apiRelatorios.acrescentar(relatorioId, secaoId, texto);
              await aoGravar();
            }}
          />
        </div>
      )}
    </>
  );
}

/**
 * O que a máquina diz acima dos dois passos, por peça.
 *
 * ⚠️ **Cada uma diz o que o prompt NÃO carrega**, e é a informação mais útil
 * das três: o capítulo 1 recebe material estreito de propósito, e sem isso
 * escrito ele pediria ao modelo um resumo que a primeira página não deve dar.
 */
const APOIO_DO_PROMPT: Record<'objeto' | 'partes', string> = {
  objeto:
    'O Hub monta o prompt com os marcos da dívida, o cliente, o nível e os nomes ' +
    'das partes. ⛔ Ele NÃO carrega bens, ônus nem passivo: a primeira página abre ' +
    'o documento e o capítulo 2 é que resume.',
  partes:
    'O Hub monta o prompt com nome, documento, qualificação e de onde veio cada ' +
    'parte. ⛔ Ele NÃO carrega patrimônio. O quadro acima continua saindo impresso ' +
    'do cadastro; o que se escreve aqui é a prosa que o apresenta.',
};
